import { z } from "zod";

/**
 * İŞ AKIŞI TANIMI ŞEMALARI (Dinamik Workflow — Kart 2).
 *
 * ⚠ Bu dosya TANIM tarafıdır: tenant'ın yeniden kullanılabilir süreç şablonu. Belirli bir
 * sözleşmede ÇALIŞAN akış ayrı bir kavramdır ve buraya karışmaz (kanonik spesifikasyon §22,
 * UI/UX §48). İkisi tek şemada toplansaydı "düzenle" komutunun neyi değiştirdiği belirsiz
 * kalırdı.
 */

/**
 * ⚠ TEK SÖZLÜK — hem TANIM hem ÇALIŞAN akış bunu kullanır (CLOS-1213).
 *
 * Kanon bu iki amacı `NEGOTIATION` / `FINAL_APPROVAL` diye adlandırıyor; depodaki adlar
 * `external_release` / `final_internal`. **Kavramlar aynı**, adlar farklı: gönderim onayı
 * müzakere turunun çıktısıdır, nihai iç onay imza öncesi kabuldür.
 *
 * İkinci bir sözlük AÇILMADI: aynı kavramın iki adı olsaydı hangi satırın hangi sözlükten
 * geldiği bir daha okunamazdı (Cross-Domain Addendum §1.3 — eşdeğer varsa yeniden
 * yaratılmaz). Kanonik terminolojiye geçiş, davranış değiştirmeyen ayrı bir adlandırma
 * kartının işi.
 */
export const WORKFLOW_PURPOSES = ["external_release", "final_internal"] as const;
export type WorkflowPurpose = (typeof WORKFLOW_PURPOSES)[number];

export const WORKFLOW_PURPOSE_NAMES: Record<WorkflowPurpose, string> = {
  external_release: "Gönderim Onayı",
  final_internal: "Nihai İç Onay",
};

/** Aşamanın "kim" sorusu — üçüncü bir atama tipi bu kapsamda YOK (§7). */
export const WORKFLOW_ASSIGNMENT_TYPES = ["user_based", "role_based"] as const;
export type WorkflowAssignmentType = (typeof WORKFLOW_ASSIGNMENT_TYPES)[number];

/** Aşamanın "ne yapabilir" sorusu. Varsayılan `full` (§13). */
export const WORKFLOW_ACTION_TYPES = ["full", "review", "revision", "approval"] as const;
export type WorkflowActionType = (typeof WORKFLOW_ACTION_TYPES)[number];

/** Rol bazlı aşamanın "hangi katılımcı koşulunda biter" sorusu (§9.3). */
export const WORKFLOW_COMPLETION_POLICIES = ["all", "any", "partial"] as const;
export type WorkflowCompletionPolicy = (typeof WORKFLOW_COMPLETION_POLICIES)[number];

/** `PARTIAL` tam olarak birini kullanır (§12). */
export const WORKFLOW_PARTIAL_MODES = ["specific_users", "required_count"] as const;
export type WorkflowPartialMode = (typeof WORKFLOW_PARTIAL_MODES)[number];

export const WORKFLOW_DEFINITION_STATUSES = ["draft", "published", "archived"] as const;
export type WorkflowDefinitionStatus = (typeof WORKFLOW_DEFINITION_STATUSES)[number];

/**
 * ⚠ Kullanıcıya gösterilen adlar burada, arayüzde değil (UI/UX §77): etiket string'i domain
 * mantığı olarak kullanılamaz, ama iki yerde ayrı yazılırsa biri gün gelip diğerinden ayrışır.
 */
export const WORKFLOW_ACTION_TYPE_NAMES: Record<WorkflowActionType, string> = {
  full: "Tam Yetkili",
  review: "İnceleme",
  revision: "Revizyon",
  approval: "Onay",
};

export const WORKFLOW_COMPLETION_POLICY_NAMES: Record<WorkflowCompletionPolicy, string> = {
  all: "Tüm Kullanıcılar",
  any: "Herhangi Bir Kullanıcı",
  partial: "Kısmi Tamamlama",
};

export const WORKFLOW_ASSIGNMENT_TYPE_NAMES: Record<WorkflowAssignmentType, string> = {
  user_based: "Kullanıcı Bazlı",
  role_based: "Rol Bazlı",
};

export const WORKFLOW_DEFINITION_STATUS_NAMES: Record<WorkflowDefinitionStatus, string> = {
  draft: "Taslak",
  published: "Yayında",
  archived: "Arşivlenmiş",
};

export const createWorkflowSchema = z.object({
  /**
   * ⚠ AMAÇ ZORUNLU, VARSAYILANI YOK (CLOS-1213). Varsayılan koysaydık, amacını hiç
   * düşünmeden kurulan her akış "gönderim onayı" olur ve kanonun ayırdığı iki karar
   * kurulum anında karışırdı.
   */
  purpose: z.enum(WORKFLOW_PURPOSES, "Akışın amacı seçilmelidir"),
  name: z
    .string()
    .trim()
    .min(1, "İş akışı adı zorunludur")
    .max(120, "İş akışı adı en fazla 120 karakter olabilir"),
  description: z
    .string()
    .trim()
    .max(1000, "Açıklama en fazla 1000 karakter olabilir")
    .optional()
    .nullable(),
});
export type CreateWorkflowInput = z.infer<typeof createWorkflowSchema>;

/**
 * Liste satırı.
 *
 * ⚠ `draftVersionNumber` ve `publishedVersionNumber` AYRI: aynı akışın yayında bir sürümü
 * ve üzerinde çalışılan bir taslağı aynı anda olabilir (UI/UX §6.4). Tek "sürüm" alanı
 * gösterseydik kullanıcı hangisinin canlı olduğunu bilemezdi.
 */
export type WorkflowSummary = {
  id: string;
  name: string;
  /** ⚠ Amaç listede taşınıyor: "varsayılan yap" komutu HANGİ amacın varsayılanı olduğunu
   * bu alandan okuyor. İstemcinin ayrıca seçmesi istenseydi, akışın kendi amacıyla
   * çelişen bir varsayılan atanabilirdi. */
  purpose: WorkflowPurpose;
  description: string | null;
  status: WorkflowDefinitionStatus;
  draftVersionNumber: number | null;
  publishedVersionNumber: number | null;
  /**
   * BU AKIŞ KİRACI VARSAYILANI MI (CLOS-1204).
   *
   * ⚠ Ölçülen kusur: kullanıcı "Varsayılan yap" diyor, satır yazılıyor ve sayfa hiçbir şey
   * göstermiyordu. Karar verilebiliyordu ama GÖRÜLEMİYORDU — yanlış atamayı fark etmek de
   * bu yüzden zordu. Alan API'de hiç yoktu, yani arayüz bilgiyi alamıyordu bile.
   *
   * ⚠ AD DAR SEÇİLDİ: "bu akış herhangi bir varsayılan mı" DEMİYOR. Tür düzeyi
   * (`contract_type` dolu) varsayılanlar ayrı bir eksen; onların atama yüzeyi bugün yok ve
   * canlıda hiç satırı yok. Geniş bir ad koysaydık, tür düzeyi varsayılanlar geldiğinde
   * alan sessizce yanlış cevap vermeye başlardı.
   */
  isTenantDefault: boolean;
  createdByName: string;
  createdAt: string;
  updatedAt: string;
};

/**
 * TASLAK AŞAMA LİSTESİ YAZMA GİRDİSİ.
 *
 * ### ⚠ Bütün liste tek istekte
 *
 * Builder bir canvas: kullanıcı aşama ekliyor, sıralıyor, siliyor. Granüler uçlar
 * (ekle/sil/sırala) her sürükleme için ayrı istek üretir ve arada kalan bir hata sırayı
 * yarım bırakırdı. Tek yazma, taslağın o anki hâlini bütün olarak kaydediyor.
 *
 * ### ⚠ Sıra GÖNDERİM SIRASINDAN türüyor
 *
 * İstemci ayrıca `orderIndex` göndermiyor: iki kaynak (dizi sırası ve alan) bir gün
 * ayrışır ve hangisinin doğru olduğu belirsiz kalırdı.
 *
 * ### ⚠ Çapraz alan kuralları BURADA ve VERİTABANINDA
 *
 * Aynı kurallar `workflow_stage_definitions` CHECK'lerinde de var. İkisi birden: şema
 * kullanıcıya anlaşılır Türkçe hata veriyor, CHECK ise şemayı atlayan hiçbir yolun
 * çelişkili satır yazamayacağını garanti ediyor.
 */
const stageInputSchema = z
  .object({
    name: z
      .string()
      .trim()
      .min(1, "Aşama adı zorunludur")
      .max(120, "Aşama adı en fazla 120 karakter olabilir"),
    instruction: z
      .string()
      .trim()
      .max(1000, "Aşama açıklaması en fazla 1000 karakter olabilir")
      .optional()
      .nullable(),
    assignmentType: z.enum(WORKFLOW_ASSIGNMENT_TYPES, { message: "Atama tipi seçilmelidir" }),
    /** ⚠ Varsayılan `full`: seçilmeyen işlem tipi "belirsiz" bırakılmaz (§14.4). */
    actionType: z.enum(WORKFLOW_ACTION_TYPES).default("full"),
    completionPolicy: z.enum(WORKFLOW_COMPLETION_POLICIES).nullish(),
    partialMode: z.enum(WORKFLOW_PARTIAL_MODES).nullish(),
    requiredCount: z.coerce.number().int().nullish(),
    targetUserIds: z.array(z.uuid()).default([]),
    targetRoleIds: z.array(z.uuid()).default([]),
    /** Yalnız `PARTIAL / SPECIFIC_USERS`: tamamlaması ZORUNLU kişiler. */
    requiredUserIds: z.array(z.uuid()).default([]),
  })
  .superRefine((asama, ctx) => {
    const hata = (path: string, message: string) =>
      ctx.addIssue({ code: "custom", path: [path], message });

    if (asama.assignmentType === "user_based") {
      if (asama.targetUserIds.length === 0) {
        hata("targetUserIds", "Kullanıcı bazlı aşamada en az bir kullanıcı seçilmelidir");
      }
      if (asama.targetRoleIds.length > 0) {
        hata("targetRoleIds", "Kullanıcı bazlı aşamaya rol atanamaz");
      }
      /**
       * ⚠ Kullanıcı bazlı aşamada tamamlanma kuralı SEÇİLEMEZ: mevcut ürün kararında
       * seçilen kullanıcıların tamamı gerekir (§8.3). Seçenek sunmak, olmayan bir esnekliği
       * varmış gibi gösterirdi.
       */
      if (asama.completionPolicy) {
        hata(
          "completionPolicy",
          "Kullanıcı bazlı aşamada tamamlanma kuralı seçilmez; seçilen kullanıcıların tamamı gerekir",
        );
      }
      return;
    }

    if (asama.targetRoleIds.length === 0) {
      hata("targetRoleIds", "Rol bazlı aşamada en az bir rol seçilmelidir");
    }
    if (asama.targetUserIds.length > 0) {
      hata("targetUserIds", "Rol bazlı aşamaya doğrudan kullanıcı atanamaz");
    }
    if (!asama.completionPolicy) {
      hata("completionPolicy", "Rol bazlı aşamada tamamlanma kuralı seçilmelidir");
      return;
    }

    if (asama.completionPolicy !== "partial") {
      if (asama.partialMode) hata("partialMode", "Kısmi mod yalnız kısmi tamamlamada seçilir");
      if (asama.requiredCount != null) {
        hata("requiredCount", "Gerekli kişi sayısı yalnız kısmi tamamlamada belirlenir");
      }
      if (asama.requiredUserIds.length > 0) {
        hata("requiredUserIds", "Belirli kullanıcılar yalnız kısmi tamamlamada seçilir");
      }
      return;
    }

    if (!asama.partialMode) {
      hata("partialMode", "Kısmi tamamlamada belirli kullanıcılar ya da kişi sayısı seçilmelidir");
      return;
    }
    if (asama.partialMode === "specific_users") {
      if (asama.requiredUserIds.length === 0) {
        hata("requiredUserIds", "En az bir kullanıcı seçilmelidir");
      }
      if (asama.requiredCount != null) {
        hata("requiredCount", "Belirli kullanıcı seçilirken kişi sayısı belirlenmez");
      }
      return;
    }
    if (asama.requiredCount == null || asama.requiredCount < 1) {
      hata("requiredCount", "Gerekli kişi sayısı en az 1 olmalıdır");
    }
    if (asama.requiredUserIds.length > 0) {
      hata("requiredUserIds", "Kişi sayısı belirlenirken ayrıca kullanıcı seçilmez");
    }
  });

export const saveWorkflowStagesSchema = z.object({
  /** ⚠ Boş liste GEÇERLİ: kullanıcı taslağı sıfırlayabilmeli. Yayın kapısı ayrı iş. */
  stages: z.array(stageInputSchema).max(50, "Bir iş akışında en fazla 50 aşama olabilir"),
});
export type SaveWorkflowStagesInput = z.infer<typeof saveWorkflowStagesSchema>;

/** Aşama görünümü — hedefler kimlik listesi olarak dönüyor, çözümleme çalışma zamanının işi. */
export type WorkflowStageView = {
  id: string;
  orderIndex: number;
  name: string;
  instruction: string | null;
  assignmentType: WorkflowAssignmentType;
  actionType: WorkflowActionType;
  completionPolicy: WorkflowCompletionPolicy | null;
  partialMode: WorkflowPartialMode | null;
  requiredCount: number | null;
  targetUserIds: string[];
  targetRoleIds: string[];
  requiredUserIds: string[];
};

/** Tanım detayı — üzerinde çalışılan TASLAK sürüm ve onun aşamaları. */
export type WorkflowDetail = WorkflowSummary & {
  draftVersionId: string | null;
  stages: WorkflowStageView[];
};

/**
 * SÖZLEŞMEDE İŞ AKIŞI BAŞLATMA (Dinamik Workflow Kart 2).
 *
 * ⚠ `versionId` AÇIK gönderiliyor, "current" diye çözülmüyor: akış değişmez bir versiyona
 * çakılıyor ve istemcinin gördüğü versiyon ile sunucunun seçtiği versiyon ayrışırsa,
 * kullanıcı okumadığı bir metni onaya göndermiş olurdu.
 *
 * ⚠ Son tarih ZORUNLU (ADR-0065): son tarihi olmayan bir süreç "gecikmiş" olamaz ve
 * Bekleyen İşlemler'in gecikme mekanizmasının tamamen dışında kalır.
 */

/**
 * SÖZLEŞMEDE HANGİ AKIŞ GEÇERLİ — okuma sözleşmesi (CLOS-1202).
 *
 * ### ⚠ Ekran bu soruyu SORMAK zorunda, kullanıcıya SORDURMAMAK için
 *
 * Akış seçimi çözümleme hiyerarşisinden geliyor (CLOS-1201: sözleşmeye özel → tür
 * varsayılanı → kiracı varsayılanı). Ekranda kullanıcıya tanım listesi açıp seçtirseydik
 * varsayılan anlamını yitirir ve aynı soru iki yerden cevaplanırdı.
 *
 * ### ⚠ İKİ AMAÇ AYRI AYRI çözülüyor
 *
 * Gönderim Onayı ile Nihai İç Onay iki ayrı karardır (Karar Eki K-13/14/15) ve hiyerarşi
 * her biri için farklı bir tanıma çıkabilir. Tek bir "geçerli akış" döndürseydik ekran
 * hangi amacı gösterdiğini bilemezdi.
 *
 * ### ⚠ `blockedReason` KULLANICIYA GÖSTERİLİR
 *
 * Çözümleme başarısız olabilir (atanmış akış arşivlenmiş, hiç varsayılan yok). Sebebi
 * ekranda söylemek, kullanıcıyı sunucunun 409'uyla sürprize uğratmaktan iyidir — kartın
 * AC-3'ü bunu açıkça istiyor.
 */
export const workflowResolutionViewSchema = z.object({
  definitionId: z.uuid().nullable(),
  /** Çözülen tanımın adı; çözülemediyse `null`. */
  definitionName: z.string().nullable(),
  /** Hangi basamaktan geldi: sözleşme · tür · kiracı · hiçbiri. */
  level: z.enum(["contract", "contract_type", "tenant", "none"]),
  blockedReason: z.string().nullable(),
  /**
   * Bu amaç ŞU AN başlatılabilir mi?
   *
   * ⚠ Çözülmüş bir akışın olması yetmiyor: Nihai İç Onay yalnız MUTABIK versiyonda
   * başlatılabilir (K-17) ve çalışan bir akış varken ikincisi açılamaz. Sunucu bunları
   * zaten reddediyor; ekran ÖNCEDEN söyleyebilsin diye karar burada da veriliyor.
   */
  canStart: z.boolean(),
  /** `canStart` false ise insan diliyle sebebi; true ise `null`. */
  blockedStartReason: z.string().nullable(),
});
export type WorkflowResolutionView = z.infer<typeof workflowResolutionViewSchema>;

/**
 * Çözümleme sorgusu — versiyon ZORUNLU.
 *
 * ⚠ Query alanları kaynak önekli okunuyor (`query.versionId`); şema adı da bunu söylüyor.
 */
export const workflowResolutionQuerySchema = z.object({
  versionId: z.uuid({ message: "Geçerli bir versiyon seçilmelidir" }),
});

/** Sözleşmenin iki amaç için çözümlenmiş akış görünümü. */
export const contractWorkflowResolutionSchema = z.object({
  external_release: workflowResolutionViewSchema,
  final_internal: workflowResolutionViewSchema,
});
export type ContractWorkflowResolution = z.infer<typeof contractWorkflowResolutionSchema>;

export const startContractWorkflowSchema = z.object({
  versionId: z.uuid({ message: "Geçerli bir versiyon seçilmelidir" }),
  workflowDefinitionId: z.uuid({ message: "Geçerli bir iş akışı seçilmelidir" }),
  dueDate: z.iso.datetime({ message: "Geçerli bir son tarih girin" }),
  /**
   * ⚠ ZORUNLU, VARSAYILANI YOK (Karar Eki K-13/K-14/K-15).
   *
   * Varsayılan koysaydık kayıt "onaylandı" der ama neyin onaylandığını söylemezdi. Gönderim
   * Onayı müzakere sırasında verilir ve karşı tarafın kabulünü gerektirmez; Nihai İç Onay
   * mutabakat SONRASI verilir ve imza önkoşuludur. Aynı satırdan ikisini de okumak isteyen
   * biri hangisine baktığını bilemezdi.
   */
  purpose: z.enum(WORKFLOW_PURPOSES, { message: "Akışın amacı seçilmelidir" }),
});
export type StartContractWorkflowInput = z.infer<typeof startContractWorkflowSchema>;

/**
 * VARSAYILAN AKIŞ ATAMA (CLOS-1201).
 *
 * ⚠ `contractType` NULL ise KİRACI varsayılanı, dolu ise TÜR varsayılanı. Ayrı iki uç
 * yazsaydık aynı kavram iki kapıdan yönetilir ve biri gün gelip diğerinden ayrışırdı.
 */
export const setWorkflowDefaultSchema = z.object({
  /** ⚠ Varsayılan AMACA göre: kiracının iki amacı için iki ayrı varsayılanı olabilir. */
  purpose: z.enum(WORKFLOW_PURPOSES, "Akışın amacı seçilmelidir"),
  contractType: z
    .string()
    .trim()
    .min(1, "Sözleşme türü boş olamaz")
    .max(200, "Sözleşme türü en fazla 200 karakter olabilir")
    .nullable(),
});
export type SetWorkflowDefaultInput = z.infer<typeof setWorkflowDefaultSchema>;

/** ⚠ `null` GEÇERLİ: sözleşmeye özel atamayı KALDIRIR ve hiyerarşi varsayılanlara döner. */
export const assignContractWorkflowSchema = z.object({
  workflowDefinitionId: z.uuid({ message: "Geçerli bir iş akışı seçilmelidir" }).nullable(),
});
export type AssignContractWorkflowInput = z.infer<typeof assignContractWorkflowSchema>;
